import { getEventsUrl } from './core/client';
import {
  RealtimeEventSchema,
  EmailSchema,
  FolderSchema,
  SyncJobSchema,
  SyncStatusSchema,
} from './schemas';
import type { Email, RealtimeEvent } from '@/types/email';

export { getEventsUrl };

// Re-export the RealtimeEvent type from types/email.ts for consistency
export type { RealtimeEvent } from '@/types/email';

// ============================================================================
// Event Stream Parsing
// ============================================================================

// Todo realtime events are intentionally NOT zod-validated here — the todo store
// owns that payload contract. We still keep an allow-list of the known subtypes
// so a *new* server-side todo subtype shows up as contract drift rather than a
// silent pass-through. (calendar.* and note.* DO have schemas in
// RealtimeEventSchema, so a valid one of those never reaches the drift branch.)
const KNOWN_TODO_EVENT_TYPES = new Set<string>([
  'todo.task.created', 'todo.task.updated', 'todo.task.deleted',
  'todo.list.created', 'todo.list.updated', 'todo.list.deleted',
  'todo.project.created', 'todo.project.updated', 'todo.project.deleted',
  'todo.tag.created', 'todo.tag.updated', 'todo.tag.deleted',
]);

// Event-type prefixes the client owns a handler for. An undeclared type under
// one of these prefixes is treated as drift (logged + passed through); a type
// outside all of them is rejected.
const KNOWN_EVENT_PREFIXES = ['todo.', 'note.', 'calendar.'];

export function parseEventStreamData(data: string): RealtimeEvent | null {
  try {
    const parsed = JSON.parse(data);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const type = String(parsed.type || '');

    // Try to parse with the discriminated union schema
    const result = RealtimeEventSchema.safeParse({ ...parsed, type });
    if (result.success) {
      return result.data as RealtimeEvent;
    }

    // Handle known event types that need special processing
    switch (type) {
      case 'email.received':
      case 'email.updated': {
        const emailResult = EmailSchema.safeParse(parsed.email);
        if (emailResult.success) {
          return {
            type: type as 'email.received' | 'email.updated',
            accountId: String(parsed.accountId),
            email: emailResult.data as unknown as Email,
          } as RealtimeEvent;
        }
        break;
      }

      case 'email.moved':
        return {
          type: 'email.moved',
          accountId: String(parsed.accountId),
          emailId: String(parsed.emailId),
          fromFolderId: String(parsed.fromFolderId),
          toFolderId: String(parsed.toFolderId),
        };

      case 'email.deleted':
        return {
          type: 'email.deleted',
          accountId: String(parsed.accountId),
          emailId: String(parsed.emailId),
          folderId: String(parsed.folderId),
        };

      case 'mailbox.counts': {
        type ParsedFolder = {
          id: string;
          accountId: string;
          name: string;
          path: string;
          role?: string | null;
          unreadCount: number;
          totalCount?: number;
        };
        const mailboxes: ParsedFolder[] = Array.isArray(parsed.mailboxes)
          ? parsed.mailboxes
              .map((m: unknown) => {
                const mailboxResult = FolderSchema.safeParse(m);
                return mailboxResult.success ? (mailboxResult.data as ParsedFolder) : null;
              })
              .filter((m: ParsedFolder | null): m is ParsedFolder => m !== null)
          : [];
        return {
          type: 'mailbox.counts',
          accountId: String(parsed.accountId),
          mailboxes: mailboxes.map((m: ParsedFolder) => ({
            id: m.id,
            accountId: m.accountId,
            name: m.name,
            path: m.path,
            role: m.role,
            unreadCount: m.unreadCount,
            totalCount: m.totalCount ?? 0,
          })),
        } as RealtimeEvent;
      }

      case 'mailbox.sync.progress': {
        const processed = parsed.processed as number;
        let total = parsed.total as number | undefined;
        const remaining = parsed.remaining as number | undefined;
        // Compute total from processed + remaining if not provided directly
        if (total === undefined && remaining !== undefined) {
          total = processed + remaining;
        }
        return {
          type: 'mailbox.sync.progress',
          accountId: String(parsed.accountId),
          mailboxId: String(parsed.mailboxId),
          processed,
          total: total ?? 0,
          remaining,
          status: parsed.status,
        };
      }

      case 'folder.preferences.updated':
        return {
          type: 'folder.preferences.updated',
          accountId: String(parsed.accountId),
        };

      case 'mailbox.list.changed':
        return {
          type: 'mailbox.list.changed',
          accountId: String(parsed.accountId),
        };

      case 'sync.status': {
        const statusResult = SyncStatusSchema.safeParse(parsed.status);
        if (statusResult.success) {
          return {
            type: 'sync.status',
            status: statusResult.data,
          } as unknown as RealtimeEvent;
        }
        break;
      }

      case 'contact.stateChanged':
        return {
          type: 'contact.stateChanged',
          accountId: String(parsed.accountId),
          contactId: String(parsed.contactId),
          approvedState: parsed.approvedState as 'pending' | 'approved' | 'blocked',
          notificationsEnabled: Boolean(parsed.notificationsEnabled),
        };

      case 'attachment.downloaded':
        return {
          type: 'attachment.downloaded',
          accountId: String(parsed.accountId),
          emailId: String(parsed.emailId),
          attachmentId: String(parsed.attachmentId),
          metadata: parsed.metadata,
        } as unknown as RealtimeEvent;

      case 'sync.job.updated': {
        const jobResult = SyncJobSchema.safeParse(parsed.job);
        if (jobResult.success) {
          return {
            type: 'sync.job.updated',
            accountId: String(parsed.accountId),
            job: jobResult.data,
          } as unknown as RealtimeEvent;
        }
        break;
      }

      case 'contact.reviewRecommendation':
        return {
          type: 'contact.reviewRecommendation',
          accountId: String(parsed.accountId),
          contactId: String(parsed.contactId),
          recommendation: parsed.recommendation ?? null,
        };

      case 'operation.failed':
        return {
          type: 'operation.failed',
          accountId: String(parsed.accountId),
          operationType: String(parsed.operationType ?? ''),
          messageId: parsed.messageId != null ? String(parsed.messageId) : undefined,
          message: String(parsed.message ?? 'An operation failed.'),
        };

      case 'resync':
        return { type: 'resync' };

      default: {
        // Known todo subtype: the todo store validates its own payload, so pass
        // it through untouched (no drift warning).
        if (KNOWN_TODO_EVENT_TYPES.has(type)) {
          return parsed as RealtimeEvent;
        }
        // A type under a prefix we own a handler for, but with no matching schema
        // (calendar.*/note.* failed validation above, or a brand-new undeclared
        // subtype of any owned prefix). Surface it as contract drift but keep the
        // stream flowing — a server-side addition must never black out the client.
        if (KNOWN_EVENT_PREFIXES.some((prefix) => type.startsWith(prefix))) {
          console.warn(
            '[parseEventStreamData] Passing through prefixed realtime event with no matching schema (possible contract drift):',
            type,
            result.error?.format()
          );
          return parsed as RealtimeEvent;
        }
        // Genuinely unrecognized type: no case, no owned prefix. Reject so a
        // new/undeclared event type is caught instead of silently vanishing.
        console.warn(
          '[parseEventStreamData] Dropping unrecognized realtime event type:',
          type,
          '— add it to RealtimeEventSchema or the parse switch if the client should handle it.'
        );
        return null;
      }
    }

    // A known event type matched the switch but failed its payload validation
    // (e.g. a malformed email/job/status sub-schema). Log the zod error to debug.
    console.warn('[parseEventStreamData] Could not parse known event:', type, result.error?.format());
    return null;
  } catch (error) {
    console.warn('Failed to parse realtime event', error);
    return null;
  }
}
